'use client';

import { forwardRef, useId, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '../../Button/Button';
import { SearchIcon } from '@/assets/icons/SearchIcon';
import { CloseClearIcon } from '@/assets/icons/CloseClearIcon';
import styles from './searchInput.module.scss';

export interface SearchInputProps {
    onChange?: (value: string) => void;
    value: string;
    ariaExpanded: boolean;
    ariaControls: string;
    userTypingDelay?: number;
    maxSearchLength?: number;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
    (
        {
            value = '',
            ariaExpanded = false,
            ariaControls,
            onChange = () => {},
            userTypingDelay = 1000,
            maxSearchLength,
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const searchInputLabelId = useId();
        const [inputValue, setInputValue] = useState(value);
        const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();

        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        useEffect(() => {
            setInputValue(value);
        }, [value]);

        useEffect(() => {
            return () => {
                if (typingTimeout) {
                    clearTimeout(typingTimeout);
                }
            };
        }, [typingTimeout]);

        const clearInput = useCallback(() => {
            setInputValue('');
            onChange('');
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, [onChange]);

        const handleInputChange = useCallback(
            (newVal: string) => {
                setInputValue(newVal);
                if (typingTimeout) {
                    clearTimeout(typingTimeout);
                }
                const newTimeout = setTimeout(() => {
                    requestAnimationFrame(() => {
                        onChange(newVal);
                    });
                }, userTypingDelay);
                setTypingTimeout(newTimeout);
            },
            [typingTimeout, userTypingDelay, onChange],
        );

        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                    // Immediately trigger the onChange callback if a user presses enter
                    clearTimeout(typingTimeout);
                    onChange(inputValue);
                }
            },
            [onChange, inputValue, typingTimeout],
        );

        return (
            <div className={styles.inputWrapper}>
                <label htmlFor={searchInputLabelId}>
                    <SearchIcon className={styles.searchIcon} role="img" aria-label="Search SiriusXM" />
                </label>

                <input
                    data-searchterm={inputValue}
                    ref={inputRef}
                    type="text"
                    placeholder="What are you looking for?"
                    value={inputValue}
                    onChange={(event) => handleInputChange(event.target.value)}
                    onKeyDown={handleKeyDown}
                    className={styles.searchInput}
                    id={searchInputLabelId}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={ariaExpanded}
                    aria-controls={ariaControls}
                    autoComplete="off"
                    maxLength={maxSearchLength}
                />
                {inputValue && (
                    <Button
                        type="button"
                        onClick={clearInput}
                        variant="tertiary"
                        fitting="hug-contents"
                        size="small"
                        tertiaryNoPadding
                        className={styles.clearButton}
                        aria-label="Clear search input"
                    >
                        <CloseClearIcon aria-hidden="true" />
                    </Button>
                )}
            </div>
        );
    },
);

SearchInput.displayName = 'SearchInput';
